import React, { useState } from 'react';
import { X } from 'lucide-react';

interface Props {
  totalAmount: number;
  onComplete: (paymentMethod: string, amountReceived: number) => void;
  onClose: () => void;
}

const paymentMethods = [
  { id: 'cash', label: 'Cash', icon: '💵' },
  { id: 'card', label: 'Card', icon: '💳' },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
  { id: 'qr_payment', label: 'QR Payment', icon: '📱' },
];

const POSPaymentModal: React.FC<Props> = ({ totalAmount, onComplete, onClose }) => {
  const [selectedMethod, setSelectedMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState<string>(totalAmount.toString());
  const [processing, setProcessing] = useState(false);

  const changeAmount = Math.max(0, Number(amountReceived) - totalAmount);
  const isValid = Number(amountReceived) >= totalAmount || selectedMethod !== 'cash';

  const handleComplete = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const received = selectedMethod === 'cash' ? Number(amountReceived) : totalAmount;
      await onComplete(selectedMethod, received);
    } finally {
      setProcessing(false);
    }
  };

  // Quick cash amounts
  const quickAmounts = [
    totalAmount,
    Math.ceil(totalAmount / 1000) * 1000,
    Math.ceil(totalAmount / 5000) * 5000,
    Math.ceil(totalAmount / 10000) * 10000,
    50000,
    100000,
  ].filter((v, i, a) => a.indexOf(v) === i && v >= totalAmount).slice(0, 4);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl w-full max-w-md p-6 shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Payment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Total */}
        <div className="text-center mb-6">
          <p className="text-sm text-gray-400">Total Amount</p>
          <p className="text-3xl font-bold text-green-400">{totalAmount.toLocaleString()} MMK</p>
        </div>

        {/* Payment Methods */}
        <div className="grid grid-cols-4 gap-2 mb-6">
          {paymentMethods.map((method) => (
            <button
              key={method.id}
              onClick={() => setSelectedMethod(method.id)}
              className={`p-3 rounded-lg text-center transition ${
                selectedMethod === method.id
                  ? 'bg-blue-600 ring-2 ring-blue-400'
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <span className="text-xl">{method.icon}</span>
              <p className="text-xs text-white mt-1">{method.label}</p>
            </button>
          ))}
        </div>

        {/* Cash Input */}
        {selectedMethod === 'cash' && (
          <div className="space-y-3 mb-6">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Amount Received</label>
              <input
                type="number"
                value={amountReceived}
                onChange={(e) => setAmountReceived(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-xl text-center font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            {/* Quick amounts */}
            <div className="grid grid-cols-4 gap-2">
              {quickAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => setAmountReceived(amount.toString())}
                  className="py-2 bg-gray-700 hover:bg-gray-600 rounded text-xs text-white font-medium transition"
                >
                  {amount.toLocaleString()}
                </button>
              ))}
            </div>

            {/* Change */}
            {changeAmount > 0 && (
              <div className="bg-gray-700 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-400">Change</p>
                <p className="text-2xl font-bold text-yellow-400">{changeAmount.toLocaleString()} MMK</p>
              </div>
            )}
          </div>
        )}

        {/* Complete Button */}
        <button
          onClick={handleComplete}
          disabled={!isValid || processing}
          className="w-full py-4 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:text-gray-400 text-white font-bold rounded-lg text-lg transition"
        >
          {processing ? 'Processing...' : `Complete Sale`}
        </button>
      </div>
    </div>
  );
};

export default POSPaymentModal;
